import {
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  query,
  orderByChild,
  equalTo,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { Invitation } from '../../types';
import { dateToMs, msToDate, snapshotToArray, stripUndefined } from './helpers';

const PATH = 'invitations';

type Unsubscribe = () => void;

const fromRtdb = (id: string, data: any): Invitation => ({
  ...data,
  id,
  createdAt: msToDate(data.createdAt) as Date,
  expiresAt: msToDate(data.expiresAt) as Date,
});

const toRtdb = (invitation: Invitation): any => {
  const { id: _id, ...rest } = invitation;
  return {
    ...rest,
    createdAt: dateToMs(invitation.createdAt),
    expiresAt: dateToMs(invitation.expiresAt),
  };
};

const sortByCreatedAtDesc = (invitations: Invitation[]): Invitation[] =>
  [...invitations].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

/**
 * Generate a unique invitation token
 */
export const generateInviteToken = (): string => {
  return `inv_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .substring(2, 15)}`;
};

export const getAllInvitations = async (): Promise<Invitation[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return sortByCreatedAtDesc(
    snapshotToArray<Invitation>(snapshot.val(), fromRtdb)
  );
};

export const getInvitationByToken = async (
  token: string
): Promise<Invitation | null> => {
  const q = query(ref(rtdb, PATH), orderByChild('token'), equalTo(token));
  const snapshot = await get(q);
  const invitations = snapshotToArray<Invitation>(snapshot.val(), fromRtdb);
  return invitations[0] ?? null;
};

/**
 * Get pending invitation by email.
 * Uses orderByChild on email, then filters status in-memory (RTDB cannot do
 * compound where clauses).
 */
export const getInvitationByEmail = async (
  email: string
): Promise<Invitation | null> => {
  const q = query(
    ref(rtdb, PATH),
    orderByChild('email'),
    equalTo(email.toLowerCase())
  );
  const snapshot = await get(q);
  const invitations = snapshotToArray<Invitation>(snapshot.val(), fromRtdb);
  return invitations.find((i) => i.status === 'pending') ?? null;
};

export const saveInvitation = async (invitation: Invitation): Promise<void> => {
  await set(
    ref(rtdb, `${PATH}/${invitation.id}`),
    stripUndefined(toRtdb(invitation))
  );
};

export const updateInvitation = async (
  id: string,
  updates: Partial<Invitation>
): Promise<void> => {
  const data: any = { ...updates };
  if (updates.createdAt !== undefined) data.createdAt = dateToMs(updates.createdAt);
  if (updates.expiresAt !== undefined) data.expiresAt = dateToMs(updates.expiresAt);
  await update(ref(rtdb, `${PATH}/${id}`), stripUndefined(data));
};

export const deleteInvitation = async (invitationId: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${invitationId}`));
};

export const subscribeToInvitations = (
  callback: (invitations: Invitation[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(
        sortByCreatedAtDesc(
          snapshotToArray<Invitation>(snapshot.val(), fromRtdb)
        )
      );
    },
    onError
  );
  return () => off(r, 'value', handler);
};

/**
 * Check if an invitation is expired
 */
export const isInvitationExpired = (invitation: Invitation): boolean => {
  return new Date() > invitation.expiresAt;
};
